/**
 * Smith Protective — Prospect Detail (Iter160).
 *
 * Full 360° view of a single security-industry prospect: company facts,
 * property intel, incumbent provider, decision-makers, triggers, and
 * the opportunity/scoring engine.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Shield, Building2, MapPin, Users, DollarSign, Flame, AlertTriangle,
  Loader2, ArrowLeft, ExternalLink, Mail, Phone, Linkedin, RefreshCw,
  UserCheck, Zap, Target, Lock, Sparkles, Copy, Check, History, X,
  Send, UserPlus, MessageCircle, TrendingUp, Search, Landmark,
  Download, FileSpreadsheet, FileText, Image as ImageIcon, Wand2,
  ChevronDown, ChevronRight, Clock,
  MoreVertical,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator,
} from "../../components/ui/dropdown-menu";
import Sidebar from "../../components/Sidebar";
import AppHeader from "../../components/SmithAppHeader";
import ProspectMiniMap from "../../components/ProspectMiniMap";
import { ObjectionLibraryButton } from "./ObjectionLibrary";
import { OcpaEnrichButton } from "./OcpaEnrichButton";
import { SunbizEnrichButton, SunbizCrossRefBadge } from "./SunbizEnrichButton";
import { SunbizTimelineWidget } from "./SunbizTimelineWidget";
import SmithVisionAnalyzer from "./SmithVisionAnalyzer";
import UrgencySignalsPanel from "./UrgencySignalsPanel";
import ProspectEmailTimelineCard from "../../components/smith/ProspectEmailTimelineCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NAVY = "#0B1120";
const usd = (n) => (n ? `$${Math.round(n).toLocaleString("en-US")}` : "—");

const STATUS_OPTIONS = ["New Prospect", "Researching", "Qualified", "Contacted",
  "Meeting Booked", "Proposal Sent", "Negotiation", "Won", "Lost", "On Hold"];

export default function SmithProspectDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [outreach, setOutreach] = useState(null);
  const [outreachLoading, setOutreachLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [tone, setTone] = useState("consultative");
  const [provider, setProvider] = useState("claude");   // Iter176 — claude | gpt
  const [genImage, setGenImage] = useState(false);      // Iter176 — GPT Image 1 loading
  const [image, setImage] = useState(null);             // Iter176 — {b64, generated_at}
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [sending, setSending] = useState(false);
  const [scrapingContacts, setScrapingContacts] = useState(false);
  const [huntering, setHuntering] = useState(false);
  const [regEnriching, setRegEnriching] = useState(false);
  const [enrichingFull, setEnrichingFull] = useState(false);   // Iter180 — one-click enrich orchestrator
  const [lastEnrich, setLastEnrich] = useState(null);          // Iter180 — response for banner
  const [enrichHistory, setEnrichHistory] = useState(null);     // Iter181 — enrichment audit trail
  const [providerTimeline, setProviderTimeline] = useState(null); // Iter184 — provider change history
  const [sendingSms, setSendingSms] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [offlineBanner, setOfflineBanner] = useState(null);   // Iter269 · IDB fallback banner

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/smith/prospects/${id}`);
      setP(data);
      setOfflineBanner(null);
      // Iter269 · cache the fresh payload for offline access.
      import("../../lib/prospectCache").then(({ cacheProspect }) => cacheProspect(data));
      if (data.last_outreach?.content) {
        setOutreach({ content: data.last_outreach.content,
                      generated_at: data.last_outreach.generated_at });
      }
    } catch (e) {
      // Iter269 · fallback to IndexedDB when network fails
      try {
        const { getCachedProspect } = await import("../../lib/prospectCache");
        const cached = await getCachedProspect(id);
        if (cached) {
          setP(cached);
          setOfflineBanner({ cached_at: cached.cached_at });
          toast.info("Modo offline — mostrando dados em cache", { duration: 4000 });
          return;
        }
      } catch (_e) { /* fall through to error */ }
      toast.error(e?.response?.data?.detail || "Prospect não encontrado");
    } finally { setLoading(false); }
  };
  // Iter181 — load enrichment audit history
  const loadEnrichHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/smith/prospects/${id}/enrich-history`);
      setEnrichHistory(data);
    } catch { /* silent — not critical */ }
  };
  // Iter184 — provider change timeline
  const loadProviderTimeline = async () => {
    try {
      const { data } = await axios.get(`${API}/smith/prospects/${id}/provider-timeline`);
      setProviderTimeline(data);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); loadEnrichHistory(); loadProviderTimeline(); }, [id]);

  const changeStatus = async (v) => {
    setSaving(true);
    try {
      await axios.patch(`${API}/smith/prospects/${id}`, { pipeline_status: v });
      toast.success("Status atualizado");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar");
    } finally { setSaving(false); }
  };

  const generateOutreach = async () => {
    setOutreachLoading(true);
    setCopied(false);
    try {
      const { data } = await axios.post(`${API}/smith/prospects/${id}/outreach?tone=${tone}&provider=${provider}`, {},
        { timeout: 60000 });   // Iter249d · Hard client cap so UI never spins forever
      setOutreach(data);
      if (data?.contingency_mode) {
        // Iter249d · The backend hit its LLM timeout / provider error and
        // fell through to the deterministic template. Warn the operator
        // so they can regenerate once upstream stabilises.
        toast.warning("Claire em modo contingência — LLM instável. Você pode editar/regenerar em alguns segundos.",
                      { duration: 6000 });
      } else {
        toast.success(`Claire gerou o plano (${tone} · ${provider === "gpt" ? "GPT 5.4-mini" : "Claude 4.5"})`);
      }
    } catch (e) {
      // Iter249d · Distinguish gateway 502/504 (Cloudflare) from a real
      // backend error so the user knows to retry rather than "verify key".
      const status = e?.response?.status;
      if (status === 502 || status === 504 || e?.code === "ECONNABORTED") {
        toast.error("Servidor de IA sob pressão — tente novamente em alguns segundos.");
      } else {
        toast.error(e?.response?.data?.detail || "Claire falhou — verifique a EMERGENT_LLM_KEY");
      }
    } finally { setOutreachLoading(false); }
  };

  const generateOutreachImage = async () => {
    // Iter176 — GPT Image 1 illustration for the outreach preview.
    setGenImage(true);
    try {
      const desc = `${(p?.company?.name || "warehouse operator")} at ${p?.property?.address || "an industrial site"} in ${p?.property?.city || "Florida"} — professional Smith Protective security officer in navy uniform standing at the loading dock, cinematic dusk lighting, photorealistic, wide angle, corporate safety brochure style`;
      const { data } = await axios.post(`${API}/smith/generate-image`,
        { prompt: desc, number_of_images: 1 });
      setImage({ b64: data.images_base64?.[0], generated_at: data.generated_at });
      toast.success("Imagem GPT Image 1 gerada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao gerar imagem");
    } finally { setGenImage(false); }
  };

  const openHistory = async () => {
    setShowHistory(true);
    try {
      const [{ data: hist }, { data: an }] = await Promise.all([
        axios.get(`${API}/smith/prospects/${id}/outreach/history`),
        axios.get(`${API}/smith/analytics/outreach`).catch(() => ({ data: null })),
      ]);
      setHistory(hist.items || []);
      setAnalytics(an);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar histórico");
    }
  };

  const downloadOutreachExport = async (fmt) => {
    try {
      const r = await axios.get(
        `${API}/smith/prospects/${id}/outreach/history/export?fmt=${fmt}`,
        { responseType: "blob" },
      );
      const link = document.createElement("a");
      link.href = URL.createObjectURL(new Blob([r.data]));
      const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "");
      link.download = `outreach_${id}_${stamp}.${fmt}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
      toast.success(`Export .${fmt} pronto · ${history.length} mensagens`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Falha no export .${fmt}`);
    }
  };

  const copyOutreach = async () => {
    if (!outreach?.content) return;
    try {
      await navigator.clipboard.writeText(outreach.content);
      setCopied(true);
      toast.success("Copiado para o clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Falha ao copiar"); }
  };

  const sendOutreach = async () => {
    if (!outreach?.content) {
      toast.error("Gere um outreach primeiro");
      return;
    }
    // Find first decision-maker email; prompt if none
    const dmEmail = (p?.decision_makers || []).find((d) => d.email)?.email;
    if (!dmEmail) {
      toast.error("Nenhum contato com e-mail — rode 'Scrape Contacts' primeiro");
      return;
    }
    if (!window.confirm(`Enviar o Cold Email desse outreach para ${dmEmail}?`)) return;
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API}/smith/prospects/${id}/outreach/send`,
        { outreach_id: outreach.id }, // if id exists, use it; else uses last_outreach
      );
      toast.success(`E-mail enviado para ${data.to} · status → Contacted`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no envio via Resend");
    } finally { setSending(false); }
  };

  const scrapeContacts = async () => {
    setScrapingContacts(true);
    try {
      const { data } = await axios.post(`${API}/smith/prospects/${id}/scrape-contacts`, {});
      if (data.added > 0) {
        toast.success(`${data.added} contato(s) adicionados`);
      } else {
        toast.info("Nenhum contato novo encontrado no site — tente Hunter.io");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao raspar contatos");
    } finally { setScrapingContacts(false); }
  };

  const hunterEnrich = async () => {
    setHuntering(true);
    try {
      const { data } = await axios.post(`${API}/smith/prospects/${id}/hunter-enrich`, {});
      if (data.added > 0) toast.success(`Hunter.io adicionou ${data.added} contato(s)`);
      else toast.info(data.reason === "no_hunter_matches"
        ? "Hunter.io não encontrou emails para esse domínio"
        : "Nenhum contato novo");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no Hunter.io — verifique HUNTER_API_KEY");
    } finally { setHuntering(false); }
  };

  const registryEnrich = async () => {
    setRegEnriching(true);
    try {
      const { data } = await axios.post(`${API}/smith/prospects/${id}/registry-enrich`, {});
      if (data.added > 0) {
        toast.success(`${data.state} registry adicionou ${data.added} officer(s)`);
      } else if (data.registry_url) {
        // Open the registry search page for manual lookup
        toast.info(`Abrindo o cadastro do ${data.state} · nenhum officer live encontrado`);
        window.open(data.registry_url, "_blank", "noopener");
      } else {
        toast.info("Nenhum officer encontrado");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no registry lookup");
    } finally { setRegEnriching(false); }
  };

  // Iter180 — one-click "Enrich Company & Property Data" orchestrator
  const enrichFull = async () => {
    setEnrichingFull(true);
    setLastEnrich(null);
    try {
      const { data } = await axios.post(`${API}/smith/prospects/${id}/enrich-full`, {});
      setLastEnrich(data);
      const parts = [];
      if (data.decision_makers_added) parts.push(`${data.decision_makers_added} DM(s)`);
      if (data.property_updated?.length)
        parts.push(`${data.property_updated.filter((k) => !k.endsWith("_estimated")).length} atributo(s) físico(s)`);
      if (data.company_updated?.length)
        parts.push(`${data.company_updated.filter((k) => !k.endsWith("_estimated")).length} campo(s) da empresa`);
      if (data.score?.delta > 0)
        parts.push(`Score +${data.score.delta}${data.score.upgraded ? " ⬆ tier upgrade" : ""}`);
      if (parts.length) toast.success("Enriched: " + parts.join(" · "));
      else toast.info("Nenhum novo campo — fontes retornaram vazio");
      if (data.source_status?.apollo === "plan_required") {
        // Iter249 · Apollo Free blocked us. If the APIFY fallback found
        // contacts we surface a POSITIVE message ("using the alternative
        // scraper") instead of the old scary warning about apollo.io/pricing.
        const apifyFound = (data.source_status?.apify_website === "ok"
                            || data.source_status?.apify_gmaps === "ok");
        if (apifyFound) {
          toast.success("Buscando via APIFY (alternativa) — Apollo Free bloqueado, usando scrapers gratuitos");
        } else {
          toast.info("Apollo Free bloqueou API — APIFY fallback rodando em segundo plano");
        }
      }
      await load();
      await loadEnrichHistory();
      await loadProviderTimeline();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no enrichment orchestrator");
    } finally { setEnrichingFull(false); }
  };

  const sendSms = async () => {
    if (!outreach?.content) {
      toast.error("Gere um outreach primeiro");
      return;
    }
    const phone = (p?.decision_makers || []).find((d) => d.phone)?.phone;
    if (!phone) {
      const manual = window.prompt("Telefone do contato (E.164, ex: +14075551234):");
      if (!manual) return;
      return doSendSms(manual);
    }
    if (!window.confirm(`Enviar o Voicemail Script como SMS para ${phone}?`)) return;
    return doSendSms(phone);
  };
  const doSendSms = async (phone) => {
    setSendingSms(true);
    try {
      const { data } = await axios.post(
        `${API}/smith/prospects/${id}/outreach/send-sms`,
        { outreach_id: outreach?.id, to_phone: phone },
      );
      toast.success(`SMS enviado · sid ${data.sid?.slice(0, 10)}…`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no envio SMS via Twilio");
    } finally { setSendingSms(false); }
  };

  if (loading || !p) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: NAVY }}>
        <Loader2 className="w-6 h-6 animate-spin text-[#DC2626]" />
      </div>
    );
  }

  const t = p.tier || {};
  const b = p.score_breakdown || {};
  const sec = p.security || {};
  const prop = p.property || {};
  const comp = p.company || {};
  const opp = p.opportunity || {};

  return (
    <div className="min-h-screen" style={{ background: NAVY, color: "#E2E8F0" }}>
      <Sidebar />
      <div className="lg:ml-[var(--al-sidebar-w,260px)]">
        <AppHeader
          testid="smith-detail-header"
          title={comp.name || "Prospect"}
          subtitle={`${(p.segment || "").replace("_", " ")} · ${prop.city || ""}, ${prop.state || ""}`}
          logo={
            <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                 style={{ background: "linear-gradient(135deg,#1E3A8A 0%,#0F172A 100%)", color: "#DC2626" }}>
              <Shield className="w-5 h-5" />
            </div>
          }
        >
          <Link to="/smith/prospects" data-testid="back-to-list"
            className="h-9 px-3 rounded-lg border border-white/15 text-[12.5px] font-bold text-white hover:border-[#DC2626] inline-flex items-center gap-1.5">
            <ArrowLeft className="w-3.5 h-3.5" /> Voltar
          </Link>
          <select value={tone} onChange={(e) => setTone(e.target.value)}
            data-testid="outreach-tone-select"
            className="h-9 px-2 rounded-lg bg-black/30 border border-white/15 text-[12px] font-bold text-white focus:border-[#8B5CF6] outline-none">
            <option value="consultative">Tone · Consultative</option>
            <option value="urgent">Tone · Urgent</option>
            <option value="empathetic">Tone · Empathetic</option>
            <option value="roi">Tone · ROI-first</option>
          </select>
          <select value={provider} onChange={(e) => setProvider(e.target.value)}
            data-testid="outreach-provider-select"
            title="AI model provider"
            className="h-9 px-2 rounded-lg bg-black/30 border border-white/15 text-[12px] font-bold text-white focus:border-[#10A37F] outline-none">
            <option value="claude">Claude 4.5</option>
            <option value="gpt">GPT 5.4-mini</option>
          </select>
          <button type="button" onClick={generateOutreach} disabled={outreachLoading}
            data-testid="claire-outreach-btn"
            className="h-9 px-3 rounded-lg font-bold text-[12.5px] text-white inline-flex items-center gap-1.5 disabled:opacity-50"
            style={{ background: "linear-gradient(135deg,#8B5CF6 0%,#6D28D9 100%)" }}>
            {outreachLoading
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Claire gerando…</>
              : <><Sparkles className="w-3.5 h-3.5" /> Claire · Outreach</>}
          </button>
          {/* Iter249e · Objection Library — 9 respostas curadas para colar em 1 clique */}
          <ObjectionLibraryButton testid="objection-library-open" />
          <button type="button" onClick={generateOutreachImage} disabled={genImage}
            data-testid="outreach-image-btn"
            title="Ilustrar com GPT Image 1 (guarda Smith no local do prospect)"
            className="h-9 px-3 rounded-lg font-bold text-[12.5px] text-white inline-flex items-center gap-1.5 disabled:opacity-50"
            style={{ background: "linear-gradient(135deg,#10A37F 0%,#065F46 100%)" }}>
            {genImage
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Gerando…</>
              : <><ImageIcon className="w-3.5 h-3.5" /> Illustrate</>}
          </button>
          <button type="button" onClick={enrichFull} disabled={enrichingFull}
            data-testid="enrich-full-btn"
            title="One-click enrichment: Apollo + Hunter + Sunbiz + County records + physical heuristics + score refresh"
            className="h-9 px-3 rounded-lg font-bold text-[12.5px] text-white inline-flex items-center gap-1.5 disabled:opacity-50"
            style={{ background: "linear-gradient(135deg,#8B5CF6 0%,#6D28D9 100%)" }}>
            {enrichingFull ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
            Enrich Data
          </button>
          <button type="button" onClick={load} disabled={saving}
            data-testid="refresh-detail"
            className="h-9 px-3 rounded-lg border border-white/15 text-[12.5px] font-bold hover:border-[#DC2626] inline-flex items-center gap-1.5">
            <RefreshCw className={`w-3.5 h-3.5 ${saving ? "animate-spin" : ""}`} /> Refresh
          </button>
          {/* Iter183 — Overflow menu for secondary actions */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button"
                      data-testid="header-more-menu"
                      aria-label="Mais ações"
                      title="Mais ações"
                      className="h-9 w-9 rounded-lg border border-white/15 text-[12.5px] font-bold hover:border-[#DC2626] inline-flex items-center justify-center">
                <MoreVertical className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 z-[100]"
              data-testid="header-more-menu-content">
              <DropdownMenuItem onClick={openHistory}
                data-testid="header-more-outreach-history"
                className="gap-2 cursor-pointer">
                <History className="w-4 h-4 text-violet-400" /> Outreach History
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={scrapeContacts} disabled={scrapingContacts}
                data-testid="header-more-scrape-contacts"
                className="gap-2 cursor-pointer">
                {scrapingContacts ? <Loader2 className="w-4 h-4 animate-spin text-sky-400" /> : <UserPlus className="w-4 h-4 text-sky-400" />}
                Scrape Contacts
              </DropdownMenuItem>
              <DropdownMenuItem onClick={hunterEnrich} disabled={huntering}
                data-testid="header-more-hunter"
                className="gap-2 cursor-pointer">
                {huntering ? <Loader2 className="w-4 h-4 animate-spin text-orange-400" /> : <Search className="w-4 h-4 text-orange-400" />}
                Hunter.io
              </DropdownMenuItem>
              <DropdownMenuItem onClick={registryEnrich} disabled={regEnriching}
                data-testid="header-more-registry"
                className="gap-2 cursor-pointer">
                {regEnriching ? <Loader2 className="w-4 h-4 animate-spin text-blue-400" /> : <Landmark className="w-4 h-4 text-blue-400" />}
                State Registry
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </AppHeader>

        <main className="px-3 sm:px-6 lg:px-8 py-4 sm:py-6 max-w-[1400px] mx-auto space-y-4 pb-24 lg:pb-6"
              data-testid="smith-prospect-detail">
          {/* Iter269 · Offline cache banner */}
          {offlineBanner && (
            <div className="rounded-xl border p-2.5 flex items-center gap-2 text-[12px]"
                 data-testid="offline-cache-banner"
                 style={{ background: "rgba(245,158,11,0.12)", borderColor: "rgba(245,158,11,0.4)" }}>
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
              <span className="text-amber-100 font-bold">Modo offline</span>
              <span className="text-slate-300">
                Dados de {new Date(offlineBanner.cached_at).toLocaleString()}
              </span>
              <button onClick={load}
                      data-testid="offline-cache-retry"
                      className="ml-auto h-7 px-2 rounded text-[11px] font-bold text-amber-200 hover:bg-white/10">
                Tentar atualizar
              </button>
            </div>
          )}
          {/* Hero — score + status + ACV */}
          <div className="rounded-2xl border p-4 sm:p-5 grid grid-cols-1 lg:grid-cols-[auto_1fr_auto] gap-4 items-center"
               style={{ background: `linear-gradient(135deg,${t.color}22 0%,rgba(15,23,42,0.7) 60%)`, borderColor: `${t.color}55` }}>
            <div className="flex items-center gap-3">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-[26px] font-extrabold tabular-nums"
                   style={{ background: t.color, color: "white" }}
                   data-testid="detail-score">
                {p.opportunity_score}
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-widest text-slate-400 font-bold">Opportunity Score</div>
                <div className="text-[15px] font-extrabold" style={{ color: t.color }}>
                  {t.icon} {t.label}
                </div>
                {p.icp_match?.on_icp && (
                  <div className="mt-1 inline-flex items-center gap-1 text-[10.5px] px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-bold uppercase tracking-wider">
                    <Target className="w-3 h-3" /> On-ICP · {p.icp_match.icp_name}
                  </div>
                )}
              </div>
            </div>
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-widest text-slate-400 font-bold mb-1">Pipeline Status</div>
              <select value={p.pipeline_status || "New Prospect"}
                      onChange={(e) => changeStatus(e.target.value)}
                      disabled={saving}
                      data-testid="detail-status-select"
                      className="w-full max-w-sm h-10 px-3 rounded-lg bg-black/30 border border-white/15 text-[13.5px] font-bold text-white focus:border-[#DC2626] outline-none">
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-widest text-slate-400 font-bold">Est. Annual Contract Value</div>
              <div className="text-[26px] font-extrabold text-emerald-400 tabular-nums" data-testid="detail-acv">
                {usd(opp.acv_est)}
              </div>
              <div className="text-[11px] text-slate-500">
                {opp.estimated_guards ? `${opp.estimated_guards} guards · ` : ""}{(opp.coverage || "—").replace("_", " ")}
              </div>
            </div>
          </div>

          {/* Claire Outreach panel (Iter161) */}
          {(outreach || outreachLoading) && (
            <section className="rounded-2xl border p-4 sm:p-5"
                     data-testid="outreach-panel"
                     style={{ background: "linear-gradient(135deg,rgba(139,92,246,0.10) 0%,rgba(15,23,42,0.7) 60%)",
                              borderColor: "rgba(139,92,246,0.35)" }}>
              <div className="flex items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#8B5CF6]" />
                  <h3 className="font-extrabold text-white text-[13.5px]">Claire · 3-Touch Outreach Plan</h3>
                  {outreach?.tone && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#8B5CF6]/20 text-[#A78BFA] font-bold uppercase tracking-wider">
                      {outreach.tone}
                    </span>
                  )}
                  {outreach?.generated_at && (
                    <span className="text-[10.5px] text-slate-500">
                      · {new Date(outreach.generated_at).toLocaleString()}
                    </span>
                  )}
                  {outreach?.contingency_mode && (
                    /* Iter249d · Contingency banner. Fired when the LLM
                       timed out and the backend served the deterministic
                       template. Ops can hit "Claire · Outreach" again
                       once upstream recovers. */
                    <span data-testid="outreach-contingency-badge"
                          title="LLM sob pressão. Este é um template seguro — clique em Claire · Outreach de novo em alguns segundos para regenerar com IA."
                          className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/25 text-amber-300 font-bold uppercase tracking-wider inline-flex items-center gap-1">
                      ⚠ Contingência
                    </span>
                  )}
                </div>
                {outreach && (
                  <div className="flex items-center gap-1.5">
                    <button type="button" onClick={sendOutreach} disabled={sending || outreachLoading}
                      data-testid="outreach-send-btn"
                      className="h-8 px-3 rounded-lg font-bold text-[11.5px] text-white inline-flex items-center gap-1.5 disabled:opacity-50"
                      style={{ background: "linear-gradient(135deg,#10B981 0%,#047857 100%)" }}>
                      {sending
                        ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Enviando…</>
                        : <><Send className="w-3.5 h-3.5" /> Resend</>}
                    </button>
                    <button type="button" onClick={sendSms} disabled={sendingSms || outreachLoading}
                      data-testid="outreach-sms-btn"
                      title="Send Voicemail Script as SMS via Twilio"
                      className="h-8 px-3 rounded-lg font-bold text-[11.5px] text-white inline-flex items-center gap-1.5 disabled:opacity-50"
                      style={{ background: "linear-gradient(135deg,#EC4899 0%,#BE185D 100%)" }}>
                      {sendingSms
                        ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> SMS…</>
                        : <><MessageCircle className="w-3.5 h-3.5" /> SMS</>}
                    </button>
                    <button type="button" onClick={copyOutreach}
                      data-testid="outreach-copy-btn"
                      className="h-8 px-3 rounded-lg border border-white/15 text-[11.5px] font-bold hover:border-[#8B5CF6] inline-flex items-center gap-1.5">
                      {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                      {copied ? "Copiado" : "Copiar"}
                    </button>
                    <Link to={`/smith/outreach?prospect_id=${id}&tab=compose`}
                      data-testid="outreach-open-hub-btn"
                      title="Abrir no hub Outreach AI (Claire + templates + analytics)"
                      className="h-8 px-3 rounded-lg text-[11.5px] font-bold text-white inline-flex items-center gap-1.5"
                      style={{ background: "linear-gradient(135deg,#DC2626 0%,#7F1D1D 100%)" }}>
                      <Wand2 className="w-3.5 h-3.5" /> Outreach AI
                    </Link>
                  </div>
                )}
              </div>
              {outreachLoading ? (
                <div className="h-24 flex items-center justify-center">
                  <Loader2 className="w-5 h-5 animate-spin text-[#8B5CF6]" />
                  <span className="ml-2 text-[12.5px] text-slate-300">Claire está lendo o dossiê do prospect…</span>
                </div>
              ) : (
                <pre className="whitespace-pre-wrap text-[12.5px] leading-relaxed text-slate-100 font-sans max-h-[520px] overflow-y-auto"
                     data-testid="outreach-content">
                  {outreach?.content}
                </pre>
              )}
            </section>
          )}

          {/* Iter176 — GPT Image 1 illustration preview */}
          {image?.b64 && (
            <section
              data-testid="outreach-image-preview"
              className="rounded-2xl border p-3 sm:p-4"
              style={{ background: "rgba(16,163,127,0.05)", borderColor: "rgba(16,163,127,0.30)" }}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <ImageIcon className="w-4 h-4 text-[#10A37F]" />
                  <span className="text-[11px] uppercase tracking-widest text-[#10A37F] font-bold">GPT Image 1 · Illustration</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <a href={`data:image/png;base64,${image.b64}`}
                     download={`smith_outreach_${id}.png`}
                     data-testid="outreach-image-download"
                     className="h-7 px-2 rounded-md border border-emerald-500/40 text-[10.5px] font-bold text-emerald-300 hover:border-emerald-400 hover:text-emerald-200 inline-flex items-center gap-1">
                    <Download className="w-3 h-3" /> PNG
                  </a>
                  <button type="button" onClick={() => setImage(null)}
                          data-testid="outreach-image-close"
                          className="w-7 h-7 rounded-md border border-white/15 flex items-center justify-center text-slate-400 hover:text-white hover:border-white/40">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              </div>
              <img src={`data:image/png;base64,${image.b64}`}
                   alt="GPT Image 1 illustration"
                   className="w-full rounded-lg border border-white/10 max-h-[400px] object-contain" />
            </section>
          )}

          {/* 3-column intel */}
          {lastEnrich && (
            <div data-testid="enrich-result-banner"
                 className="rounded-2xl border p-3 sm:p-4 grid grid-cols-2 sm:grid-cols-5 gap-3"
                 style={{ background: "linear-gradient(135deg,rgba(139,92,246,0.15) 0%,rgba(15,23,42,0.7) 60%)",
                          borderColor: "rgba(139,92,246,0.45)" }}>
              <div>
                <div className="text-[9.5px] uppercase tracking-widest text-slate-400 font-bold">DMs added</div>
                <div className="text-[18px] font-extrabold text-white tabular-nums">
                  +{lastEnrich.decision_makers_added || 0}
                </div>
              </div>
              <div>
                <div className="text-[9.5px] uppercase tracking-widest text-slate-400 font-bold">Property</div>
                <div className="text-[18px] font-extrabold text-white tabular-nums">
                  +{(lastEnrich.property_updated || []).filter((k) => !k.endsWith("_estimated")).length}
                </div>
              </div>
              <div>
                <div className="text-[9.5px] uppercase tracking-widest text-slate-400 font-bold">Company</div>
                <div className="text-[18px] font-extrabold text-white tabular-nums">
                  +{(lastEnrich.company_updated || []).filter((k) => !k.endsWith("_estimated")).length}
                </div>
              </div>
              <div>
                <div className="text-[9.5px] uppercase tracking-widest text-slate-400 font-bold">Score</div>
                <div className="text-[18px] font-extrabold tabular-nums"
                     style={{ color: lastEnrich.score?.delta > 0 ? "#10B981" : "#94A3B8" }}>
                  {lastEnrich.score?.old || 0} → {lastEnrich.score?.new || 0}
                  {lastEnrich.score?.delta > 0 && (
                    <span className="text-[11px] ml-1 text-emerald-400">(+{lastEnrich.score.delta})</span>
                  )}
                </div>
              </div>
              <div className="col-span-2 sm:col-span-1">
                <div className="text-[9.5px] uppercase tracking-widest text-slate-400 font-bold">Sources</div>
                <div className="flex flex-wrap gap-1 mt-0.5">
                  {Object.entries(lastEnrich.source_status || {}).map(([k, v]) => {
                    const ok = v === "ok";
                    const planRequired = v === "plan_required";
                    const disabled = ["disabled", "no_domain", "not_florida",
                                       "no_company_name", "no_results"].includes(v);
                    let cls = "bg-red-500/25 text-red-300";
                    if (ok) cls = "bg-emerald-500/25 text-emerald-300";
                    else if (planRequired) cls = "bg-amber-500/25 text-amber-300 hover:bg-amber-500/40";
                    else if (disabled) cls = "bg-slate-500/25 text-slate-300";
                    const content = <>{k} {ok ? "✓" : planRequired ? "⚑" : disabled ? "○" : "!"}</>;
                    if (planRequired && k === "apollo") {
                      return (
                        <a key={k}
                           href="https://www.apollo.io/pricing"
                           target="_blank" rel="noreferrer"
                           title="Apollo Free plan bloqueia API — clique para fazer upgrade"
                           data-testid={`enrich-source-${k}`}
                           className={"text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded inline-flex items-center gap-1 cursor-pointer transition " + cls}>
                          {content}
                          <ExternalLink className="w-2.5 h-2.5" />
                        </a>
                      );
                    }
                    return (
                      <span key={k} title={v}
                            data-testid={`enrich-source-${k}`}
                            className={"text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded " + cls}>
                        {content}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* 3-column intel */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <Panel title="Company" icon={Building2}>
              {/* Iter251 · Sunbiz enrich (FL only) — top-right of card */}
              <div className="flex justify-end -mt-1 mb-1">
                <SunbizEnrichButton
                  prospectId={p.id}
                  state={prop.state}
                  onEnriched={() => load()} />
              </div>
              <SunbizCrossRefBadge
                hasParcelId={!!prop.ocpa_parcel_id}
                sunbizDocNumber={comp.sunbiz_document_number}
                sunbizDetailUrl={comp.sunbiz_detail_url} />
              <Row label="Industry" v={comp.industry} />
              <Row label="NAICS" v={comp.naics} />
              <Row label="Employees" v={comp.employees?.toLocaleString()} estimated={comp.employees_estimated} />
              <Row label="Revenue est." v={usd(comp.revenue_est)} estimated={comp.revenue_est_estimated} />
              <Row label="Size bucket" v={comp.size_bucket} />
              {comp.website && (
                <a href={`https://${comp.website.replace(/^https?:\/\//, "")}`} target="_blank" rel="noreferrer"
                   data-testid="detail-website-link"
                   className="mt-2 inline-flex items-center gap-1 text-[11.5px] text-[#DC2626] hover:underline">
                  {comp.website} <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </Panel>

            <Panel title="Property Intel" icon={MapPin}>
              {/* Iter250 · OCPA Enrich button — top-right of Property Intel.
                  Fills sqft_building, sqft_lot, year_built, property_use,
                  zoning, owner_name straight from Orange County FL data. */}
              <div className="flex justify-end -mt-1 mb-1">
                <OcpaEnrichButton
                  prospectId={p.id}
                  address={prop.address}
                  state={prop.state}
                  onEnriched={() => load()} />
              </div>
              <ProspectMiniMap prospect={p} />
              <div className="mt-3 space-y-0">
                <Row label="Address" v={prop.address} />
                <Row label="City / State" v={`${prop.city || ""}, ${prop.state || ""} ${prop.zip || ""}`} />
                <Row label="Type" v={prop.type} />
                <Row label="Building sqft" v={prop.sqft_building?.toLocaleString()} />
                <Row label="Lot sqft" v={prop.sqft_lot?.toLocaleString()} estimated={prop.sqft_lot_estimated} />
                <Row label="Entrances" v={prop.entrances} estimated={prop.entrances_estimated} />
                <Row label="Truck docks" v={prop.truck_docks} estimated={prop.truck_docks_estimated} />
                <Row label="Parking" v={prop.parking_capacity?.toLocaleString()} estimated={prop.parking_capacity_estimated} />
                <Row label="24/7 ops" v={prop.is_24_7 ? "✅ Yes" : (prop.is_24_7_candidate ? "🔎 Likely" : "—")} />
                <Row label="Yard / outdoor" v={prop.has_yard ? "✅ Yes" : "—"} estimated={prop.has_yard_estimated} />
                <Row label="Fenced" v={prop.fenced_perimeter ? "✅ Yes" : "—"} />
                <Row label="Gatehouse" v={prop.gatehouse ? "✅ Yes" : "—"} />
              </div>
              {/* Iter288 · OCPA Sales History + Tax Roll history — populated
                  by the OCPA scraper enhancement, best-effort. Only renders
                  when at least one non-empty list is present. */}
              {(Array.isArray(prop.sales_history) && prop.sales_history.length > 0) && (
                <div className="mt-4 pt-3 border-t border-white/10" data-testid="ocpa-sales-history">
                  <div className="text-[10.5px] uppercase tracking-widest text-slate-300 font-black mb-2 flex items-center gap-2 flex-wrap">
                    <Clock className="w-3 h-3 text-emerald-400" />
                    Sales History <span className="text-slate-500 font-normal">· {prop.sales_history.length}</span>
                    {/* Iter300 · Investor Signal — persists on prop.sales_delta */}
                    {prop.sales_delta?.multiplier >= 2 && (
                      <span data-testid="ocpa-sales-delta-badge"
                        title={`Latest sale is ${prop.sales_delta.multiplier}× the prior sale (${prop.sales_delta.prior_date} → ${prop.sales_delta.latest_date}${prop.sales_delta.years_gap ? ` · ${prop.sales_delta.years_gap}yr gap` : ""})`}
                        className={`inline-flex items-center gap-1 text-[9.5px] font-black uppercase tracking-widest px-2 py-0.5 rounded border ${
                          prop.sales_delta.tier === "hot"
                            ? "bg-rose-500/25 text-rose-100 border-rose-400/60"
                            : "bg-amber-500/25 text-amber-100 border-amber-400/60"
                        }`}>
                        <TrendingUp className="w-2.5 h-2.5" />
                        Investor Signal · {prop.sales_delta.multiplier}× last sale
                      </span>
                    )}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-[11px]">
                      <thead>
                        <tr className="text-left text-slate-500 border-b border-white/10">
                          <th className="py-1 pr-3 font-bold">Date</th>
                          <th className="py-1 pr-3 font-bold">Price</th>
                          <th className="py-1 pr-3 font-bold">Instrument</th>
                          <th className="py-1 pr-3 font-bold">Book/Page</th>
                          <th className="py-1 font-bold">Qual.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {prop.sales_history.map((s, idx) => (
                          <tr key={`${s.sale_date}-${idx}`}
                              data-testid={`ocpa-sales-row-${idx}`}
                              className={`border-b border-white/5 last:border-0 ${
                                idx === 0 && prop.sales_delta?.multiplier >= 2
                                  ? "bg-amber-500/10"
                                  : ""
                              }`}>
                            <td className="py-1 pr-3 tabular-nums text-slate-200">{s.sale_date}</td>
                            <td className="py-1 pr-3 tabular-nums font-bold text-emerald-300">
                              {s.sale_price ? `$${Number(s.sale_price).toLocaleString()}` : "—"}
                            </td>
                            <td className="py-1 pr-3 text-slate-300 truncate max-w-[160px]">{s.instrument || "—"}</td>
                            <td className="py-1 pr-3 text-slate-400 font-mono text-[10px]">{s.book_page || "—"}</td>
                            <td className="py-1">
                              {s.qualified === true && (
                                <span className="text-[9.5px] font-black uppercase tracking-widest px-1 py-0.5 rounded bg-emerald-500/20 text-emerald-300">Q</span>
                              )}
                              {s.qualified === false && (
                                <span className="text-[9.5px] font-black uppercase tracking-widest px-1 py-0.5 rounded bg-amber-500/20 text-amber-300">U</span>
                              )}
                              {s.qualified === undefined && <span className="text-slate-500">—</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {(Array.isArray(prop.tax_roll_history) && prop.tax_roll_history.length > 0) && (
                <div className="mt-4 pt-3 border-t border-white/10" data-testid="ocpa-tax-roll">
                  <div className="text-[10.5px] uppercase tracking-widest text-slate-300 font-black mb-2 flex items-center gap-1">
                    <Clock className="w-3 h-3 text-sky-400" />
                    Tax Roll <span className="text-slate-500 font-normal">· {prop.tax_roll_history.length} yrs</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-[11px]">
                      <thead>
                        <tr className="text-left text-slate-500 border-b border-white/10">
                          <th className="py-1 pr-3 font-bold">Year</th>
                          <th className="py-1 pr-3 font-bold">Just Value</th>
                          <th className="py-1 pr-3 font-bold">Assessed</th>
                          <th className="py-1 pr-3 font-bold">Taxable</th>
                          <th className="py-1 font-bold">Taxes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {prop.tax_roll_history.map((r, idx) => (
                          <tr key={r.year}
                              data-testid={`ocpa-tax-roll-row-${idx}`}
                              className="border-b border-white/5 last:border-0">
                            <td className="py-1 pr-3 tabular-nums text-slate-200 font-bold">{r.year}</td>
                            <td className="py-1 pr-3 tabular-nums text-slate-300">
                              {r.just_value ? `$${Number(r.just_value).toLocaleString()}` : "—"}
                            </td>
                            <td className="py-1 pr-3 tabular-nums text-slate-300">
                              {r.assessed_value ? `$${Number(r.assessed_value).toLocaleString()}` : "—"}
                            </td>
                            <td className="py-1 pr-3 tabular-nums text-slate-300">
                              {r.taxable_value ? `$${Number(r.taxable_value).toLocaleString()}` : "—"}
                            </td>
                            <td className="py-1 tabular-nums text-amber-300 font-bold">
                              {r.taxes ? `$${Number(r.taxes).toLocaleString()}` : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </Panel>

            <Panel title="Existing Security" icon={Lock}>
              <Row label="Current provider" v={sec.current_provider || <span className="italic text-slate-500">No evidence</span>} />
              <Row label="Provider confidence" v={sec.provider_status} />
              <Row label="Complexity" v={sec.complexity_score} />
              <Row label="Replacement potential" v={sec.incumbent_replacement_potential} />
              <div className="mt-2">
                <div className="text-[10.5px] uppercase tracking-widest text-slate-400 font-bold mb-1">Installed technology</div>
                <div className="flex flex-wrap gap-1">
                  {(sec.existing_technology || []).length === 0 && <span className="text-[11px] text-slate-500 italic">—</span>}
                  {(sec.existing_technology || []).map((t) => (
                    <span key={t} className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/10 text-slate-200">
                      {t.replace("_", " ")}
                    </span>
                  ))}
                </div>
              </div>
            </Panel>
          </div>

          {/* Enrichment History (Iter181 · collapsible Iter244) */}
          {enrichHistory && enrichHistory.count > 0 && (
            <EnrichmentHistoryPanel prospectId={id} history={enrichHistory} />
          )}

          {/* Iter252 · Sunbiz Compliance Timeline (FL only) */}
          <SunbizTimelineWidget prospectId={p.id} prospectState={prop.state} />

          {/* Iter261 · Urgency / Foreclosure signals — heuristic over cached Sunbiz+OCPA */}
          <UrgencySignalsPanel prospectId={p.id} />

          {/* Iter258 · Claude Vision — analyse property photos & floor plans */}
          <SmithVisionAnalyzer prospectId={p.id} />

          {/* Iter329 · Email timeline (Resend events) */}
          <ProspectEmailTimelineCard prospectId={p.id} />

          {/* Provider Timeline (Iter184) */}
          {providerTimeline && providerTimeline.count > 1 && (
            <Panel title={`Provider Timeline (${providerTimeline.count})`} icon={Shield}
                   data-testid="provider-timeline-panel">
              <div className="text-[11px] text-slate-400 pb-3 border-b border-white/5">
                Rastreio de mudanças no <strong className="text-slate-200">Current Protective Services Provider</strong> —
                cada evento inclui fonte + evidência.
              </div>
              <ol className="mt-3 relative border-l-2 border-white/10 pl-4 space-y-3"
                  data-testid="provider-timeline-list">
                {providerTimeline.events.map((e, idx) => {
                  const isLast = idx === providerTimeline.events.length - 1;
                  const provColor = e.provider
                    ? (e.provider.startsWith("Unknown") ? "text-slate-400"
                       : e.provider.startsWith("Self-Managed") ? "text-emerald-300"
                       : "text-indigo-300")
                    : "text-slate-500";
                  return (
                    <li key={idx} className="relative"
                        data-testid={`provider-timeline-event-${idx}`}>
                      <span className={"absolute -left-[22px] top-1 w-3 h-3 rounded-full border-2 "
                        + (isLast ? "border-emerald-400 bg-emerald-400" : "border-white/30 bg-slate-900")} />
                      <div className="flex items-baseline justify-between gap-2 flex-wrap">
                        <div className="text-[11px] text-slate-400 tabular-nums">
                          {e.at
                            ? new Date(e.at).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })
                            : "Agora"}
                          {e.triggered_by && <span className="text-slate-500"> · {e.triggered_by}</span>}
                          <span className="ml-1 text-[9.5px] uppercase tracking-wider px-1 py-0.5 rounded bg-white/5 text-slate-400">
                            {e.source}
                          </span>
                        </div>
                        {e.confidence > 0 && (
                          <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-indigo-500/25 text-indigo-200 tabular-nums">
                            {e.confidence}% confidence
                          </span>
                        )}
                      </div>
                      <div className={"text-[13px] font-extrabold mt-0.5 " + provColor}>
                        {e.provider || <span className="italic text-slate-500">— sem provedor —</span>}
                      </div>
                      {e.note && <div className="text-[10.5px] text-slate-500 italic">{e.note}</div>}
                      {e.evidence && (
                        <div className="mt-1 text-[10.5px] text-slate-400 bg-black/30 rounded px-2 py-1 border border-white/5 truncate">
                          <span className="text-amber-300 font-bold uppercase text-[9px] tracking-wider mr-1">Evidence:</span>
                          {`"${e.evidence.slice(0, 160)}${e.evidence.length > 160 ? "…" : ""}"`}
                        </div>
                      )}
                      {e.source_url && (
                        <a href={e.source_url} target="_blank" rel="noreferrer"
                           data-testid={`provider-timeline-source-${idx}`}
                           className="mt-1 inline-flex items-center gap-1 text-[10.5px] text-[#DC2626] hover:underline">
                          Fonte <ExternalLink className="w-2.5 h-2.5" />
                        </a>
                      )}
                    </li>
                  );
                })}
              </ol>
            </Panel>
          )}

          {/* Decision makers + Triggers */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <Panel title="Decision-Makers" icon={Users}>
              {(p.decision_makers || []).length === 0 ? (
                <p className="text-[11.5px] text-slate-500 italic">No decision-makers captured yet.</p>
              ) : (
                <ul className="space-y-2.5" data-testid="detail-dms">
                  {p.decision_makers.map((dm, i) => (
                    <li key={i} className="rounded-lg border border-white/5 p-2.5 bg-white/[0.03]">
                      <div className="flex items-center justify-between gap-2 mb-0.5">
                        <div className="font-bold text-white text-[13px]">{dm.name}</div>
                        <span className="text-[10px] uppercase tracking-wider text-slate-400">{dm.department || dm.source || ""}</span>
                      </div>
                      <div className="text-[11.5px] text-slate-300">{dm.title}</div>
                      <div className="mt-1 flex items-center gap-2 text-[11px]">
                        {dm.email && (
                          <a href={`mailto:${dm.email}`} className="inline-flex items-center gap-1 text-[#DC2626] hover:underline">
                            <Mail className="w-3 h-3" /> {dm.email}
                          </a>
                        )}
                        {dm.phone && (
                          <a href={`tel:${dm.phone}`} className="inline-flex items-center gap-1 text-slate-300 hover:text-white">
                            <Phone className="w-3 h-3" /> {dm.phone}
                          </a>
                        )}
                        {dm.linkedin && (
                          <a href={dm.linkedin.startsWith("http") ? dm.linkedin : `https://${dm.linkedin}`}
                             target="_blank" rel="noreferrer"
                             className="inline-flex items-center gap-1 text-slate-300 hover:text-white">
                            <Linkedin className="w-3 h-3" /> LinkedIn
                          </a>
                        )}
                        {!dm.linkedin && dm.linkedin_search_url && (
                          <a href={dm.linkedin_search_url}
                             target="_blank" rel="noreferrer"
                             data-testid={`dm-linkedin-search-${i}`}
                             className="inline-flex items-center gap-1 text-[#0EA5E9] hover:text-white">
                            <Linkedin className="w-3 h-3" /> Find on LinkedIn
                          </a>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>

            <Panel title="Trigger Events" icon={Zap}>
              {(p.triggers || []).length === 0 ? (
                <p className="text-[11.5px] text-slate-500 italic">No trigger events detected.</p>
              ) : (
                <ul className="space-y-2" data-testid="detail-triggers">
                  {p.triggers.map((tr, i) => (
                    <li key={i} className="rounded-lg border border-amber-500/20 p-2.5 bg-amber-500/[0.05]">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[10.5px] font-extrabold uppercase tracking-wider text-amber-300">
                          {tr.type.replace("_", " ")}
                        </span>
                        <span className="text-[10px] text-slate-400">{tr.confidence}</span>
                      </div>
                      <div className="text-[12.5px] text-slate-200 mt-1">{tr.description}</div>
                      {(tr.source || tr.url) && (
                        <div className="mt-1 text-[10.5px] text-slate-500">
                          {tr.url ? (
                            <a href={tr.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-[#DC2626] hover:underline">
                              {tr.source || tr.url} <ExternalLink className="w-2.5 h-2.5" />
                            </a>
                          ) : tr.source}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
          </div>

          {/* Score breakdown */}
          <Panel title="Score Breakdown" icon={Flame}>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2" data-testid="detail-score-breakdown">
              {Object.entries(b).map(([k, v]) => (
                <div key={k} className="rounded-lg border border-white/5 p-2 bg-white/[0.02]">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{k.replace("_", " ")}</div>
                  <div className="text-[16px] font-extrabold text-white tabular-nums">{v}</div>
                </div>
              ))}
            </div>
          </Panel>

          {/* Opportunity math */}
          <Panel title="Opportunity Math" icon={DollarSign}>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[12.5px]">
              <Row label="Types" v={(opp.types || []).map((t) => t.replace("_", " ")).join(", ") || "—"} />
              <Row label="Guards" v={opp.estimated_guards} />
              <Row label="Coverage" v={(opp.coverage || "—").replace("_", " ")} />
              <Row label="Hourly" v={opp.hourly_billing_rate ? `$${opp.hourly_billing_rate}/hr` : "—"} />
              <Row label="Monthly rev." v={usd(opp.monthly_revenue_est)} />
              <Row label="Annual (ACV)" v={usd(opp.acv_est)} />
              <Row label="ACV bucket" v={opp.acv_bucket} />
            </div>
          </Panel>
        </main>
      </div>

      {/* Outreach History drawer (Iter162) */}
      {showHistory && (
        <div className="fixed inset-0 z-50 flex" data-testid="outreach-history-drawer"
             onClick={() => setShowHistory(false)}>
          <div className="flex-1 bg-black/60" />
          <aside className="w-full sm:w-[560px] max-w-[95vw] h-full overflow-y-auto p-5 shadow-2xl"
                 onClick={(e) => e.stopPropagation()}
                 style={{ background: "#0B1120", borderLeft: "1px solid rgba(139,92,246,0.35)" }}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <History className="w-4 h-4 text-[#8B5CF6]" />
                <h3 className="font-extrabold text-white text-[14px]">Outreach History</h3>
                <span className="text-[11px] text-slate-500">· {history.length} versões</span>
              </div>
              <div className="flex items-center gap-1.5">
                {history.length > 0 && (
                  <>
                    <button type="button"
                      onClick={() => downloadOutreachExport("xlsx")}
                      title="Baixar Excel"
                      data-testid="outreach-export-xlsx"
                      className="h-8 px-2.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-[11px] font-bold text-emerald-300 hover:border-emerald-400 hover:text-emerald-200 inline-flex items-center gap-1">
                      <FileSpreadsheet className="w-3.5 h-3.5" />
                      XLSX
                    </button>
                    <button type="button"
                      onClick={() => downloadOutreachExport("csv")}
                      title="Baixar CSV"
                      data-testid="outreach-export-csv"
                      className="h-8 px-2.5 rounded-lg border border-blue-500/30 bg-blue-500/10 text-[11px] font-bold text-blue-300 hover:border-blue-400 hover:text-blue-200 inline-flex items-center gap-1">
                      <FileText className="w-3.5 h-3.5" />
                      CSV
                    </button>
                  </>
                )}
                <button type="button" onClick={() => setShowHistory(false)}
                  data-testid="close-history"
                  className="w-8 h-8 rounded-lg border border-white/15 flex items-center justify-center hover:border-[#8B5CF6]">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Analytics by tone (Iter164) */}
            {analytics?.by_tone?.length > 0 && (
              <section data-testid="outreach-analytics"
                       className="mb-4 rounded-xl border p-3"
                       style={{ background: "rgba(16,185,129,0.05)", borderColor: "rgba(16,185,129,0.25)" }}>
                <div className="flex items-center gap-1.5 mb-2">
                  <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="text-[10.5px] font-extrabold uppercase tracking-wider text-emerald-300">
                    Analytics · which tone converts
                  </span>
                  <span className="text-[10px] text-slate-500 ml-auto">
                    {analytics.totals.sent} sent · {analytics.totals.opened} opened · {analytics.totals.replied} replied
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-1 text-[11px]">
                  <div className="col-span-3 grid grid-cols-6 gap-1 text-[9.5px] uppercase text-slate-500 font-bold pb-1 border-b border-white/5">
                    <span>Tone</span><span className="text-right">Sent</span><span className="text-right">Open %</span>
                    <span className="text-right">Reply %</span><span className="text-right">Click %</span><span className="text-right">Bounce %</span>
                  </div>
                  {analytics.by_tone.map((row) => (
                    <div key={row.tone} className="col-span-3 grid grid-cols-6 gap-1 py-1 text-slate-200">
                      <span className="capitalize font-bold text-[#A78BFA]">{row.tone}</span>
                      <span className="text-right tabular-nums">{row.sent}</span>
                      <span className="text-right tabular-nums font-bold text-emerald-400">{row.open_rate}%</span>
                      <span className="text-right tabular-nums font-bold text-emerald-400">{row.reply_rate}%</span>
                      <span className="text-right tabular-nums">{row.click_rate}%</span>
                      <span className="text-right tabular-nums text-red-400">{row.bounce_rate}%</span>
                    </div>
                  ))}
                </div>
              </section>
            )}
            {history.length === 0 ? (
              <p className="text-[12.5px] text-slate-400 italic">
                Nenhum outreach gerado ainda. Escolha um tom e clique &quot;Claire · Outreach&quot; para começar.
              </p>
            ) : (
              <ul className="space-y-3" data-testid="history-list">
                {history.map((h) => (
                  <li key={h.id}
                      data-testid={`history-item-${h.id}`}
                      className="rounded-xl border p-3"
                      style={{ background: "rgba(139,92,246,0.05)", borderColor: "rgba(139,92,246,0.25)" }}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10.5px] font-extrabold uppercase tracking-wider text-[#A78BFA]">
                        Tone · {h.tone}
                      </span>
                      <span className="text-[10.5px] text-slate-500">
                        {new Date(h.generated_at).toLocaleString()}
                      </span>
                    </div>
                    <pre className="whitespace-pre-wrap text-[11.5px] leading-relaxed text-slate-200 font-sans max-h-[240px] overflow-y-auto">
                      {h.content}
                    </pre>
                    <div className="mt-2 flex gap-2">
                      <button type="button"
                        onClick={() => { setOutreach({ content: h.content, generated_at: h.generated_at, tone: h.tone }); setShowHistory(false); }}
                        className="text-[11px] font-bold text-[#A78BFA] hover:text-white">
                        Load into panel →
                      </button>
                      <button type="button"
                        onClick={() => { navigator.clipboard.writeText(h.content); toast.success("Copiado"); }}
                        className="text-[11px] font-bold text-slate-400 hover:text-white ml-auto">
                        Copiar
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}

function Panel({ title, icon: Icon, children, ...rest }) {
  return (
    <section className="rounded-2xl border p-4"
             style={{ background: "rgba(15,23,42,0.7)", borderColor: "rgba(220,38,38,0.20)" }}
             {...rest}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-[#DC2626]" />
        <h3 className="font-extrabold text-white text-[13px]">{title}</h3>
      </div>
      {children}
    </section>
  );
}

// ── Enrichment History (collapsible + paginated · Iter244) ─────────
// The section stays collapsed by default so the detail page keeps
// its 1-screen fold. State is persisted per prospect in `sessionStorage`
// so if the operator expanded it on this session, revisiting the same
// tab keeps it open. Full list is loaded once from the parent; the
// pager below trims rendering to `visible` rows (5, 10, 15, ...).
function EnrichmentHistoryPanel({ prospectId, history }) {
  const storageKey = `smith.enrichHistory.open.${prospectId}`;
  const [open, setOpen] = useState(() => {
    try { return sessionStorage.getItem(storageKey) === "1"; }
    catch { return false; }
  });
  const [visible, setVisible] = useState(5);

  const toggle = () => {
    setOpen((was) => {
      const next = !was;
      try { sessionStorage.setItem(storageKey, next ? "1" : "0"); }
      catch { /* private mode — ignore */ }
      if (!next) setVisible(5);   // reset pager on collapse
      return next;
    });
  };

  const items = history.items || [];
  const rows = items.slice(0, visible);
  const remaining = Math.max(0, items.length - rows.length);

  return (
    <section className="rounded-2xl border"
             style={{ background: "rgba(15,23,42,0.7)",
                        borderColor: "rgba(220,38,38,0.20)" }}
             data-testid="enrich-history-panel">
      {/* Header — always visible, click to toggle */}
      <button type="button"
              onClick={toggle}
              aria-expanded={open}
              data-testid="enrich-history-toggle"
              className="w-full text-left p-4 flex items-start gap-3 hover:bg-white/[0.02] rounded-2xl transition-colors">
        <div className="mt-0.5">
          {open ? <ChevronDown className="w-4 h-4 text-[#DC2626]" />
                : <ChevronRight className="w-4 h-4 text-[#DC2626]" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <History className="w-4 h-4 text-[#DC2626]" />
            <h3 className="font-extrabold text-white text-[13px]">
              Enrichment History <span className="text-slate-400 font-bold">({history.count})</span>
            </h3>
          </div>
          {/* Summary metrics — always visible */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <div className="text-[9.5px] uppercase tracking-widest text-slate-400 font-bold">Última</div>
              <div className="text-[12px] text-slate-100 tabular-nums" data-testid="enrich-last-at">
                {new Date(history.last_enriched_at).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}
              </div>
              <div className="text-[10px] text-slate-500 truncate">{history.last_enriched_by}</div>
            </div>
            <div>
              <div className="text-[9.5px] uppercase tracking-widest text-slate-400 font-bold">DMs total</div>
              <div className="text-[18px] font-extrabold text-emerald-400 tabular-nums" data-testid="enrich-total-dms">
                +{history.total_dms_added}
              </div>
            </div>
            <div>
              <div className="text-[9.5px] uppercase tracking-widest text-slate-400 font-bold">Score Δ</div>
              <div className="text-[18px] font-extrabold tabular-nums"
                   style={{ color: history.total_score_delta > 0 ? "#10B981" : "#94A3B8" }}
                   data-testid="enrich-total-score-delta">
                {history.total_score_delta > 0 ? "+" : ""}{history.total_score_delta}
              </div>
            </div>
            <div>
              <div className="text-[9.5px] uppercase tracking-widest text-slate-400 font-bold">Por fonte</div>
              <div className="flex flex-wrap gap-1 mt-0.5">
                {Object.entries(history.totals_by_source || {}).map(([src, n]) => (
                  <span key={src} data-testid={`enrich-total-${src}`}
                        className={"text-[9.5px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded "
                          + (n > 0 ? "bg-indigo-500/25 text-indigo-200" : "bg-slate-500/20 text-slate-400")}>
                    {src}: {n}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </button>

      {/* Expanded list */}
      {open && (
        <div className="px-4 pb-4">
          <ul className="space-y-2 pt-3 border-t border-white/5"
              data-testid="enrich-history-list">
            {rows.map((row) => {
              const dt = new Date(row.triggered_at);
              const scoreDelta = (row.score || {}).delta || 0;
              const upgraded = (row.score || {}).upgraded;
              return (
                <li key={row.id} className="rounded-lg border border-white/5 bg-white/[0.03] p-2.5"
                    data-testid={`enrich-history-row-${row.id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-[11px] text-slate-300 tabular-nums">
                      {dt.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}
                      <span className="text-slate-500"> · por {row.triggered_by}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-[10.5px] font-bold">
                      {row.decision_makers_added > 0 && (
                        <span className="px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300">
                          +{row.decision_makers_added} DM
                        </span>
                      )}
                      {scoreDelta > 0 && (
                        <span className="px-1.5 py-0.5 rounded bg-indigo-500/20 text-indigo-200">
                          Score +{scoreDelta}{upgraded && " ⬆"}
                        </span>
                      )}
                      {(row.property_updated || []).length > 0 && (
                        <span className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300">
                          {row.property_updated.length} property
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {Object.entries(row.source_status || {}).map(([k, v]) => {
                      const ok = v === "ok";
                      const disabled = ["disabled", "plan_required", "no_domain",
                                         "not_florida", "no_results",
                                         "no_new_fields"].includes(v);
                      return (
                        <span key={k}
                              className={"text-[9px] uppercase tracking-wider px-1 py-0.5 rounded "
                                + (ok ? "bg-emerald-500/20 text-emerald-300"
                                      : disabled ? "bg-slate-500/20 text-slate-400"
                                                 : "bg-red-500/20 text-red-300")}>
                          {k} {ok ? "✓" : "○"}
                        </span>
                      );
                    })}
                  </div>
                </li>
              );
            })}
          </ul>
          {/* Pager */}
          {remaining > 0 && (
            <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-white/5">
              <span className="text-[10.5px] text-slate-500 tabular-nums"
                    data-testid="enrich-history-pager-count">
                Mostrando {rows.length} de {items.length}
              </span>
              <div className="flex items-center gap-2">
                <button type="button"
                        onClick={() => setVisible((n) => n + 5)}
                        data-testid="enrich-history-load-more"
                        className="h-8 px-3 rounded-lg border border-white/15 text-[11px] font-extrabold uppercase tracking-widest hover:border-[#DC2626] text-slate-200">
                  Ver mais {Math.min(5, remaining)}
                </button>
                {remaining > 5 && (
                  <button type="button"
                          onClick={() => setVisible(items.length)}
                          data-testid="enrich-history-load-all"
                          className="h-8 px-3 rounded-lg text-[11px] font-extrabold uppercase tracking-widest text-slate-400 hover:text-white">
                    Ver todos ({remaining})
                  </button>
                )}
              </div>
            </div>
          )}
          {remaining === 0 && rows.length > 5 && (
            <div className="flex items-center justify-end mt-3 pt-3 border-t border-white/5">
              <button type="button"
                      onClick={() => setVisible(5)}
                      data-testid="enrich-history-collapse-list"
                      className="h-8 px-3 rounded-lg text-[11px] font-extrabold uppercase tracking-widest text-slate-400 hover:text-white">
                Mostrar apenas 5
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}


function Row({ label, v, estimated }) {
  return (
    <div className="flex items-baseline justify-between gap-2 py-1 text-[12.5px]"
         data-testid={estimated ? `row-estimated-${label.toLowerCase().replace(/\s+/g,'-')}` : undefined}>
      <span className="text-[10.5px] uppercase tracking-widest text-slate-500 font-bold inline-flex items-center gap-1">
        {label}
        {estimated && (
          <span title="Valor estimado por heurística — verifique em campo"
                className="text-[8.5px] px-1 py-0.5 rounded bg-amber-500/20 text-amber-300 font-bold tracking-wider">
            EST
          </span>
        )}
      </span>
      <span className="text-slate-100 text-right truncate">{v || <span className="italic text-slate-500">—</span>}</span>
    </div>
  );
}
